import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, ViewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { faCodeBranch, faGripDotsVertical, faImage, faPlus, faThumbTack, faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { map, startWith } from 'rxjs';

import { Media } from '@eiq/files/models';
import { GetNextId } from '@eiq/shared/utils';
import { ConfirmDialogComponent } from '@eiq/ui';

import { FileType } from '@empathy/global/data';
import { QuestionCard } from '@empathy/survey/base';

import { SideOptionTabs } from '../../../models/side-option-tabs.enum';
import { QuestionCardForm } from '../../../models/type-forms/question-card-form.model';
import { IQuestionCardForm } from '../../../models/type-forms/question-form-group.interface';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { StimulusPickerDialogComponent } from '../../stimulus/stimulus-picker/stimulus-picker-dialog.component';
import { TextEditorComponent } from '../../text-editor/text-editor.component';

@Component({
  selector: 'empathy-cards-form',
  templateUrl: 'cards-form.component.html',
  styleUrls: ['./cards-form.component.scss']
})
export class CardsFormComponent {
  @ViewChildren(TextEditorComponent) ckEditor: TextEditorComponent[];

  faCodeBranch = faCodeBranch;
  faGripDotsVertical = faGripDotsVertical;
  faImage = faImage;
  faPlus = faPlus;
  faThumbTack = faThumbTack;
  faTrashCan = faTrashCan;
  justAddedCardId: string;

  addCardAllowed$ = toSignal(
    this.cards.valueChanges.pipe(
      startWith(void 0),
      map(
        () =>
          this.cards.length === 0 ||
          this.cards.controls[this.cards.length - 1].value.text?.length > 0 ||
          !!this.cards.controls[this.cards.length - 1].value.stimulus?.mediaId
      )
    )
  );

  constructor(
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private surveyConditionalsService: SurveyConditionalsService,
    public surveyEditorService: SurveyEditorService
  ) {}

  get cards(): FormArray {
    return this.surveyEditorService.surveyEditor$().activeQuestion.get('cards') as FormArray;
  }

  onPaste(event: string[], pastedOnIndex: number): void {
    const currentCard = this.cards.at(pastedOnIndex);
    const isCurrentEmpty = !currentCard.get('text').value;

    event.forEach((text, index) => {
      if (index === 0 && isCurrentEmpty) {
        currentCard.get('text').setValue(text);
      } else {
        const insertIndex = pastedOnIndex + index + (isCurrentEmpty ? 0 : 1);
        const { id } = GetNextId(this.cards.value as { id: string }[], 'card');
        const newCard = new QuestionCardForm({ id, text } as QuestionCard);
        this.cards.insert(insertIndex, newCard.form);
      }
    });

    this.updateCardIds();
    this.cdr.detectChanges();
  }

  private updateCardIds(): void {
    this.cards.controls.forEach((control, index) => {
      control.get('id').setValue(`card_${index + 1}`);
    });
  }

  onAddCard(isLastOption: boolean, index: number = null): void {
    if (!isLastOption) {
      const card = this.cards.value[index + 1];
      const editor: DecoupledEditor = this.ckEditor.find((ed) => ed.id === card.id).editorInstance;
      editor.editing.view.focus();
      return;
    }

    if (!this.addCardAllowed$()) {
      return;
    }

    const newCard = new QuestionCardForm();
    const newCardId = GetNextId(this.cards.value, 'card');

    newCard.form.get('id').setValue(newCardId.id);
    this.justAddedCardId = newCardId.id;
    this.cards.push(newCard.form);

    this.cdr.detectChanges();
  }

  onRemoveAt(index: number, cardId: string): void {
    const affectedQuestionConditions = this.surveyConditionalsService.affectedQuestionConditionsByCardRemoved(cardId);
    const affectedHiddenQuestionConditions = this.surveyConditionalsService.affectedHiddenQuestionConditionsByCardRemoved(cardId);

    if (affectedQuestionConditions.length === 0 && affectedHiddenQuestionConditions.length === 0) {
      this.cards.removeAt(index);
      return;
    }

    const affectedConditions = affectedQuestionConditions.concat(affectedHiddenQuestionConditions);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Card',
        confirmText: `The card removed is used as a conditional setting on <strong>${affectedConditions.join(', ')}</strong>. If continue the condition/s will be removed.`,
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.removeCardAt(index, cardId);
      }
    });
  }

  onOpenConditionals(card: AbstractControl): void {
    this.surveyEditorService.surveyEditor$().activeCard = card as FormGroup<IQuestionCardForm>;
    this.surveyEditorService.activeTab$.set(SideOptionTabs.Conditions);
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.cards.controls, event.previousIndex, event.currentIndex);
  }

  onAddStimulus(optionIndex: number): void {
    const dialogRef = this.dialog.open(StimulusPickerDialogComponent, {
      data: {
        types: [FileType.ImageJpeg, FileType.ImagePng, FileType.ImageGif]
      }
    });

    dialogRef.afterClosed().subscribe((files: Media[]) => {
      if (files) {
        this.cards.at(optionIndex).get('stimulus').patchValue(files[0]);
      }
    });
  }

  onDeleteMedia(optionIndex: number): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Stimulus',
        confirmText: 'Are you sure you want to remove this stimulus?',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.cards.at(optionIndex).get('stimulus').reset();
      }
    });
  }
}
